import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw, Copy } from "lucide-react";
import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 写到 console 让用户能在浏览器开发者工具里看完整信息
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", error, info);
    this.setState({ componentStack: info.componentStack || null });

    // 上报到后端 (best-effort, 失败不影响 UI)
    try {
      void fetch("/api/client-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: error.message,
          name: error.name,
          stack: error.stack?.slice(0, 4000),
          componentStack: (info.componentStack || "").slice(0, 2000),
          url: window.location.href,
          ua: navigator.userAgent.slice(0, 200),
          ts: new Date().toISOString(),
        }),
      }).catch(() => {});
    } catch {}
  }

  copyToClipboard = () => {
    const text = [
      `Error: ${this.state.error?.name}: ${this.state.error?.message}`,
      "",
      "Stack:",
      this.state.error?.stack || "(no stack)",
      "",
      "Component Stack:",
      this.state.componentStack || "(no component stack)",
      "",
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
    ].join("\n");
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      return (
        <div className="flex items-center justify-center min-h-screen p-4 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-6">
            <AlertTriangle
              size={48}
              className="text-destructive mb-4 flex-shrink-0"
            />

            <h2 className="text-xl mb-2">An unexpected error occurred.</h2>

            {/* 错误名 + 消息 (第一行突出显示, 这是诊断的关键) */}
            {err && (
              <div className="w-full p-3 rounded bg-destructive/10 border border-destructive/40 mb-3">
                <div className="text-sm font-semibold text-destructive break-words">
                  {err.name}: {err.message}
                </div>
              </div>
            )}

            {/* Stack trace */}
            <div className="p-3 w-full rounded bg-muted overflow-auto mb-3 max-h-64">
              <div className="text-xs text-muted-foreground mb-1">Stack:</div>
              <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all">
                {err?.stack || "(no stack)"}
              </pre>
            </div>

            {/* Component stack (有助定位哪个 React 组件) */}
            {this.state.componentStack && (
              <div className="p-3 w-full rounded bg-muted overflow-auto mb-4 max-h-48">
                <div className="text-xs text-muted-foreground mb-1">Component Stack:</div>
                <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all">
                  {this.state.componentStack}
                </pre>
              </div>
            )}

            <div className="flex gap-2 flex-wrap justify-center">
              <button
                onClick={() => window.location.reload()}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg",
                  "bg-primary text-primary-foreground",
                  "hover:opacity-90 cursor-pointer"
                )}
              >
                <RotateCcw size={16} />
                Reload Page
              </button>
              <button
                onClick={this.copyToClipboard}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg",
                  "bg-muted text-foreground border border-border",
                  "hover:opacity-90 cursor-pointer"
                )}
              >
                <Copy size={16} />
                复制错误信息
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
